import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '@/store/store';

import { BusinessApi, Business, NewBusiness } from '../models';
import { mapBusinessApiToBusiness } from '../adapters';
import { PaginatedRequest, PaginatedResponse } from '@/types';

import { API_FLEET_URL } from '@/utils';

const businessService = createApi({
  reducerPath: 'businessesService',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_FLEET_URL}/commercialBusinesses`
  ,
      prepareHeaders: (headers, { getState }) => {
        const state = getState() as RootState;
  
        const token = state.auth?.auth?.accessToken || null;
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        return headers;
      }, }),
  tagTypes: ['Business', 'Businesses'],
  endpoints: (builder) => ({
    getBusiness: builder.query<Business, { businessId: string }>({
      query: ({ businessId }) => ({
        url: `/${businessId}`,
      }),
      transformResponse: (response: BusinessApi) => mapBusinessApiToBusiness(response),
      providesTags: ['Business'],
    }),
    getBusinesses: builder.query<PaginatedResponse<Business>, { search?: string; paginated: PaginatedRequest; }>({
      query: ({ search, paginated }) => ({
        url: '',
        params: {
          name: search || '',
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      transformResponse: (response: PaginatedResponse<BusinessApi>) => ({
        ...response,
        items: response.items.map(mapBusinessApiToBusiness),
      }),
      providesTags: ['Businesses'],
    }),
    createBusiness: builder.mutation<Business, NewBusiness>({
      query: (data) => ({
        url: '',
        method: 'POST',
        body: { ...data, umBusinessId: 'default' },
      }),
      invalidatesTags: ['Businesses'],
    }),
    updateBusiness: builder.mutation<Business, { businessId: string; data: Partial<Business> }>({
      query: ({ businessId, data }) => ({
        url: `/${businessId}`,
        method: 'PUT',
        body: { ...data, umBusinessId: 'default' },
      }),
      invalidatesTags: ['Business', 'Businesses'],
    }),
    deleteBusiness: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Business', 'Businesses'],
    }),
  }),
});

export default businessService;

export const {
  useGetBusinessQuery,
  useGetBusinessesQuery,
  useCreateBusinessMutation,
  useUpdateBusinessMutation,
  useDeleteBusinessMutation,
} = businessService;
